import React, { useEffect, useState, useCallback } from "react";
import { Panel } from "../components/Panel";
import { usePlayer } from "../context/PlayerContext";
import { api, apiErr } from "../api/client";
import { Gift, Coins, Zap, Sparkles, Check } from "lucide-react";
import { Sound } from "../game/audio";
import { toast } from "sonner";

export default function DailyRewards({ onClose }) {
  const { applyUser } = usePlayer();
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [claimed, setClaimed] = useState(null);

  const load = useCallback(() => api.daily().then(({ data }) => setState(data)).catch((e) => setErr(apiErr(e))), []);
  useEffect(() => { load(); }, [load]);

  const claim = async () => {
    setBusy(true); setErr(null);
    try { const { data } = await api.claimDaily(); applyUser(data.user); setClaimed(data.reward); Sound.evolution(); (data.achievements || []).forEach((a) => toast.success(`Award unlocked — ${a.name}!`)); await load(); }
    catch (e) { setErr(apiErr(e)); }
    finally { setBusy(false); }
  };

  if (!state) return <Panel title="Daily Rewards" onClose={onClose}><div className="text-slate-400 py-6 text-center">Loading...</div></Panel>;

  const icon = (t) => t === "coins" ? Coins : t === "xp" ? Zap : t === "xpboost" ? Zap : Sparkles;
  return (
    <Panel title="Daily Rewards" onClose={onClose}>
      <div className="text-slate-400 text-sm mb-1">7-day streak — validated by server time.</div>
      <div className="text-amber-400 text-xs font-bold mb-4">Current streak: {state.streak} day{state.streak === 1 ? "" : "s"}</div>
      {err && <div className="text-red-400 text-xs mb-2" data-testid="daily-error">{err}</div>}
      <div className="grid grid-cols-4 gap-2 mb-4">
        {state.rewards.map((r) => {
          const Icon = icon(r.type);
          const isNext = r.day === state.nextDay && state.canClaim;
          const past = state.streak >= r.day && !(state.canClaim && r.day === state.nextDay);
          return (
            <div key={r.day} className={`card p-2 flex flex-col items-center gap-1 relative ${isNext ? "border-amber-500 border-2 glow-pulse" : ""} ${r.day === 7 ? "col-span-1" : ""}`} data-testid={`daily-day-${r.day}`}>
              <div className="text-[10px] text-slate-400 font-bold">Day {r.day}</div>
              <Icon size={20} className={r.type === "cosmetic" ? "text-purple-400" : "text-amber-400"} />
              <div className="text-[9px] text-slate-300 text-center leading-tight">{r.label}</div>
              {past && <span className="absolute inset-0 rounded-xl bg-black/50 flex items-center justify-center"><Check className="text-green-400" size={20} /></span>}
            </div>
          );
        })}
      </div>
      {claimed && <div className="text-center text-green-400 font-bold mb-3" data-testid="daily-claimed">Claimed: {claimed.label}!</div>}
      <button disabled={!state.canClaim || busy} onClick={claim}
        className={`w-full py-3.5 rounded-xl font-black uppercase flex items-center justify-center gap-2 ${state.canClaim ? "btn-primary" : "bg-slate-800 text-slate-500"}`}
        data-testid="claim-daily-btn">
        <Gift size={20} /> {state.canClaim ? "Claim Today's Reward" : "Come Back Tomorrow"}
      </button>
    </Panel>
  );
}
